import { useEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Flame, Zap, Target, CheckCircle2, CalendarDays } from "lucide-react";

const reduceMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Animate a number from its previous value to `value`.
export function useCountUp(value, ms = 700) {
  const [shown, setShown] = useState(value ?? 0);
  const from = useRef(0);
  useEffect(() => {
    if (value == null) return;
    if (reduceMotion()) { setShown(value); from.current = value; return; }
    const start = performance.now();
    const a = from.current;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(a + (value - a) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

function Delta({ pct, invert = false, suffix = "vs last week" }) {
  if (pct == null) return <span className="text-[11px] text-slate-400">{suffix === "vs yesterday" ? "" : "No comparison yet"}</span>;
  const good = invert ? pct < 0 : pct > 0;
  const flat = pct === 0;
  const Icon = pct >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={"inline-flex items-center gap-0.5 text-[11px] font-semibold " + (flat ? "text-slate-400" : good ? "text-emerald-600" : "text-red-500")}>
      {!flat && <Icon size={12} />}{pct > 0 ? "+" : ""}{pct}% <span className="font-normal text-slate-400 ml-0.5">{suffix}</span>
    </span>
  );
}

function Ring({ pct, color, children, size = 56 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const [p, setP] = useState(0);
  useEffect(() => { const id = requestAnimationFrame(() => setP(pct ?? 0)); return () => cancelAnimationFrame(id); }, [pct]);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" className="stroke-slate-100" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" fill="none" stroke={color} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * Math.min(100, p)) / 100}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.16, 1, 0.3, 1)" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  );
}

function Tile({ icon, label, children, accent, delay = 0 }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center gap-3 hover:shadow-md hover:-translate-y-0.5 transition sw-fade-up" style={{ animationDelay: `${delay}ms` }}>
      {children[0]}
      <div className="min-w-0">
        <div className={"text-[10.5px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 " + accent}>{icon}{label}</div>
        {children[1]}
      </div>
    </div>
  );
}

const fmtMins = (m) => (m == null ? "—" : m < 60 ? `${m}m` : `${(m / 60).toFixed(m < 600 ? 1 : 0)}h`);

export default function ProductivityPanel({ insights }) {
  const t = insights?.tasks || {};
  const r = insights?.replies || {};
  const today = useCountUp(t.completed_today ?? 0);
  const week = useCountUp(t.completed_week ?? 0);
  const streak = useCountUp(t.streak_days ?? 0);
  const dayDelta = t.completed_yesterday ? Math.round(((t.completed_today - t.completed_yesterday) * 100) / t.completed_yesterday) : null;
  // Reply ring: full at ≤15 min, empty at ≥4 h.
  const replyPct = r.reply_median_min == null ? 0 : Math.max(5, Math.min(100, 100 - ((r.reply_median_min - 15) / (240 - 15)) * 100));

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="productivity-panel">
      <Tile icon={<CheckCircle2 size={11} />} label="Done today" accent="text-emerald-700" delay={0}>
        <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center shrink-0"><CheckCircle2 size={22} className="text-emerald-600" /></div>
        <div>
          <div className="text-[26px] leading-none font-extrabold text-slate-900 tabular-nums mt-1">{today}</div>
          <div className="mt-1">{dayDelta != null ? <Delta pct={dayDelta} suffix="vs yesterday" /> : <span className="text-[11px] text-slate-400">{t.completed_yesterday ?? 0} yesterday</span>}</div>
        </div>
      </Tile>

      <Tile icon={<CalendarDays size={11} />} label="This week" accent="text-blue-700" delay={40}>
        <div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center shrink-0"><CalendarDays size={21} className="text-blue-600" /></div>
        <div>
          <div className="text-[26px] leading-none font-extrabold text-slate-900 tabular-nums mt-1">{week}<span className="text-[13px] font-semibold text-slate-400"> / {t.created_week ?? 0} new</span></div>
          <div className="mt-1"><Delta pct={t.week_change_pct} /></div>
        </div>
      </Tile>

      <Tile icon={<Target size={11} />} label="On time" accent="text-violet-700" delay={80}>
        <Ring pct={t.on_time_rate ?? 0} color="#7c3aed">
          <span className="text-[13px] font-extrabold text-slate-900 tabular-nums">{t.on_time_rate == null ? "—" : `${t.on_time_rate}%`}</span>
        </Ring>
        <div className="text-[11.5px] text-slate-500 mt-1 leading-snug">
          {t.on_time_sample ? <>of {t.on_time_sample} tasks<br />done by deadline (7d)</> : "No dated tasks done yet"}
        </div>
      </Tile>

      <Tile icon={<Zap size={11} />} label="Reply time" accent="text-sky-700" delay={120}>
        <Ring pct={replyPct} color="#0284c7">
          <span className="text-[13px] font-extrabold text-slate-900 tabular-nums">{fmtMins(r.reply_median_min)}</span>
        </Ring>
        <div className="text-[11.5px] text-slate-500 mt-1 leading-snug">
          typical reply to clients
          {r.reply_median_today_min != null && <><br />today <span className="font-semibold text-slate-700">{fmtMins(r.reply_median_today_min)}</span></>}
        </div>
      </Tile>

      <Tile icon={<Flame size={11} />} label="Streak" accent="text-orange-600" delay={160}>
        <div className={"w-11 h-11 rounded-xl grid place-items-center shrink-0 " + (t.streak_days ? "bg-orange-50" : "bg-slate-50")}>
          <Flame size={24} className={t.streak_days ? "text-orange-500 sw-flicker" : "text-slate-300"} fill={t.streak_days ? "#fdba74" : "none"} />
        </div>
        <div>
          <div className="text-[26px] leading-none font-extrabold text-slate-900 tabular-nums mt-1">{streak}<span className="text-[13px] font-semibold text-slate-400"> day{t.streak_days === 1 ? "" : "s"}</span></div>
          <div className="text-[11px] text-slate-400 mt-1">{t.streak_days ? (t.completed_today ? "Kept alive today 🔥" : "Close one today to keep it") : "Close a task to start one"}</div>
        </div>
      </Tile>
    </div>
  );
}

const HEAT = ["bg-slate-100", "bg-emerald-200", "bg-emerald-300", "bg-emerald-500", "bg-emerald-700"];

export function Heatmap({ days = [] }) {
  if (!days.length) return null;
  const max = Math.max(1, ...days.map((d) => d.count));
  const level = (n) => (n === 0 ? 0 : Math.min(4, Math.ceil((n / max) * 4)));
  // Pad so the first column starts on Monday.
  const first = new Date(days[0].date + "T00:00:00");
  const pad = (first.getDay() + 6) % 7;
  const cells = [...Array(pad).fill(null), ...days];
  const total = days.reduce((s, d) => s + d.count, 0);
  return (
    <div data-testid="heatmap">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">Last 12 weeks</span>
        <span className="text-[11.5px] text-slate-500"><span className="font-bold text-slate-800 tabular-nums">{total}</span> tasks done</span>
      </div>
      <div className="grid grid-flow-col grid-rows-7 gap-[3px] w-fit">
        {cells.map((d, i) => d ? (
          <div key={d.date} title={`${new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}: ${d.count} done`}
            className={"w-3 h-3 rounded-[3px] sw-fade-in hover:ring-2 hover:ring-emerald-400 " + HEAT[level(d.count)]}
            style={{ animationDelay: `${Math.floor(i / 7) * 25}ms` }} />
        ) : <div key={`pad-${i}`} className="w-3 h-3" />)}
      </div>
      <div className="flex items-center gap-1 mt-2 text-[10.5px] text-slate-400">
        Less {HEAT.map((c) => <span key={c} className={"w-2.5 h-2.5 rounded-[2px] " + c} />)} More
      </div>
    </div>
  );
}
